import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateCustomSubscriptionRequest,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  GetSubscriptionsRequest,
  GetSubscriptionsResponse,
  RunSubscriptionResponse,
  SubscriptionDelivery,
  UpdateSubscriptionRequest,
  UpdateSubscriptionStatusRequest
} from '@urc-ui/store/api/subscriptions/types';
import { axiosBaseQuery, getEntityId, getHeaders } from '@urc-ui/store/api/base';
import { Filter, HttpMethod, Sort, Tag } from '@urc-ui/store/api/types';
import { logger } from '@urc-ui/services/logger';
import {
  PRESET_PERIOD,
  REPORT_CATEGORY,
  REPORT_PERIOD_ALIAS,
  SUBSCRIPTION_FREQUENCY,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TYPE
} from '@urc-ui/utils/types';
import dateTimeFns from '@urc-ui/components/date-input/utils/date-time-fns';
import { CreateSubscriptionState } from '@urc-ui/store/slices/create-subscription/createSubscriptionSlice';
import { CustomReportType, ReportType } from '@urc-ui/store/api/report-metadata/types';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { getSubscriptionTypeFromDelivery } from '@urc-ui/components/report-settings-card/subscription-delivery-utils';
import { getTimezone } from '@urc-ui/services/app-context';
import { getCustomReportColumns, getColumnIds } from '@urc-ui/utils/custom-report-metadata-utils';
import { getSubscriptionNameFilter, getStatusColumnFilter, getCategoryColumnFilter } from '@urc-ui/utils/column-filter-utils';
import { VirtualColId } from '@urc-ui/pages/landing/subscriptions-table/constants';
import { COLUMN_ID } from '@urc-ui/pages/landing/subscriptions-table/column-configurations';
import { isAmazonBusinessCheckboxSelected, SITE_AMAZON_BUSINESS } from '@urc-ui/utils/amazon-business-utils';
import { MRC_ACCREDITATION_STATUS_FILTER_VALUE_SIVT } from '../ross/constants/filter-constants';
import { isMRCCheckboxSelected } from '@urc-ui/utils/mrc-utils';

export const DEFAULT_SORT_CONFIG: Sort = {
  columnName: 'SUBSCRIPTION_AUDIT_MODIFIED_ON',
  order: 'DESC'
};

export const buildGetSubscriptionsRequest = (
  pageSize: number,
  pageOffset: number,
  filterModel?: { [key: string]: any },
  sortModel?: Sort,
  nextToken?: string
) => {
  const filters: Filter[] = [];
  const filterGroup = filterModel ? (filterModel as Record<string, any>) : {};

  if (filterModel) {
    Object.keys(filterGroup).forEach(filterType => {
      if (filterType === VirtualColId.QuickFilterVirtualColumnId) {
        filters.push(getSubscriptionNameFilter(filterModel[filterType].filter));
      } else if (filterType === COLUMN_ID.STATUS) {
        filters.push(getStatusColumnFilter(filterModel[filterType].values as string[]));
      } else if (filterType === COLUMN_ID.REPORT_CATEGORY) {
        filters.push(getCategoryColumnFilter(filterModel[filterType].values as string[]));
      }
    });
  }

  return {
    sort: sortModel ? sortModel : DEFAULT_SORT_CONFIG,
    filters: filters ? filters : undefined,
    pagination: {
      pageLimit: pageSize,
      pageOffset
    },
    ...(nextToken && { nextToken })
  };
};

export const buildGetSubscriptionByIdRequest = (subscriptionId: string) => {
  return {
    filters: [
      {
        column_name: 'SUBSCRIPTION_ID',
        filter_type: 'EQUAL',
        values: [subscriptionId]
      }
    ]
  };
};

/**
 * Builds a UTC subscription delivery object from a marketplace zoned subscription delivery.
 * Note that the ISO date should NOT be suffixed with Z for zoned time.
 * @param subscriptionDelivery - subscriptionDelivery in marketplace timezone
 * @param timezone - timezone
 */
export const buildUTCSubscriptionDelivery = (subscriptionDelivery: SubscriptionDelivery, timezone: string) => {
  if (subscriptionDelivery.deliveryMonth && subscriptionDelivery.deliveryDate && subscriptionDelivery.deliveryYear) {
    const year = `${subscriptionDelivery.deliveryYear}`.padStart(4, '0');
    const month = dateTimeFns.getMonthNumber(subscriptionDelivery.deliveryMonth);
    const date = `${subscriptionDelivery.deliveryDate}`.padStart(2, '0');
    const hour = `${subscriptionDelivery.hour}`.padStart(2, '0');
    const minute = `${subscriptionDelivery.minute}`.padStart(2, '0');
    const utcDate = zonedTimeToUtc(`${year}-${month}-${date} ${hour}:${minute}:00`, timezone);

    return {
      ...subscriptionDelivery,
      deliveryDate: utcDate.getUTCDate(),
      deliveryMonth: dateTimeFns.getMonthName(utcDate.getUTCMonth()),
      deliveryYear: utcDate.getUTCFullYear(),
      hour: utcDate.getUTCHours(),
      minute: utcDate.getUTCMinutes(),
      scheduledEndDate: utcDate.getTime()
    };
  }

  if (subscriptionDelivery.scheduledEndDate) {
    const utcDate = zonedTimeToUtc(subscriptionDelivery.scheduledEndDate, timezone);
    return {
      ...subscriptionDelivery,
      scheduledEndDate: utcDate.getTime()
    };
  }

  return subscriptionDelivery;
};

/**
 * Builds a marketplace zoned subscription delivery object from a UTC subscription delivery.
 * Note that the ISO date should be suffixed with Z for zoned time.
 * @param subscriptionDelivery - subscriptionDelivery in marketplace timezone
 * @param timezone - timezone
 */
export const buildZonedTimeSubscriptionDelivery = (subscriptionDelivery: SubscriptionDelivery, timezone: string) => {
  if (subscriptionDelivery.deliveryMonth && subscriptionDelivery.deliveryDate && subscriptionDelivery.deliveryYear) {
    const year = `${subscriptionDelivery.deliveryYear}`.padStart(4, '0');
    const month = dateTimeFns.getMonthNumber(subscriptionDelivery.deliveryMonth);
    const date = `${subscriptionDelivery.deliveryDate}`.padStart(2, '0');
    const hour = `${subscriptionDelivery.hour ?? '00'}`.padStart(2, '0');
    const minute = `${subscriptionDelivery.minute ?? '00'}`.padStart(2, '0');

    const zonedTime = utcToZonedTime(`${year}-${month}-${date} ${hour}:${minute}:00Z`, timezone);

    return {
      ...subscriptionDelivery,
      deliveryDate: zonedTime.getDate(),
      deliveryMonth: dateTimeFns.getMonthName(zonedTime.getMonth()),
      deliveryYear: zonedTime.getFullYear(),
      scheduledEndDate: zonedTime.getTime(),
      hour: zonedTime.getHours(),
      minute: zonedTime.getMinutes()
    };
  }

  if (subscriptionDelivery.frequency !== SUBSCRIPTION_FREQUENCY.SINGLE && subscriptionDelivery.scheduledEndDate) {
    const utcDate = utcToZonedTime(subscriptionDelivery.scheduledEndDate, timezone);
    return {
      ...subscriptionDelivery,
      scheduledEndDate: utcDate.getTime()
    };
  }

  return subscriptionDelivery;
};

export const buildCreateSubscriptionRequest = (
  createSubscriptionPayload: CreateSubscriptionState,
  reportConfig: ReportType | undefined,
  timezone: string
) => {
  if (!reportConfig || !reportConfig.templateId || !reportConfig.templateGuidance || !reportConfig.filterTypes) {
    const errorMessage = 'invalid report config';
    logger.logFatalMessage(errorMessage, reportConfig);
    throw new Error(errorMessage);
  }

  const reportCategory = getReportCategory(createSubscriptionPayload);
  const request = buildBaseCreateSubscriptionRequest(createSubscriptionPayload, reportConfig, timezone);

  // ToDo: use same set of fields for create and create custom - https://sim.amazon.com/issues/RCMRX-6846
  return {
    ...request,
    taktReportType: reportConfig.taktReportType,
    reportProgramType: reportCategory,
    reportUnit: createSubscriptionPayload.timeUnit
  };
};

export const buildCreateCustomSubscriptionRequest = (
  createSubscriptionPayload: CreateSubscriptionState,
  reportConfig: ReportType | undefined,
  customReportType: CustomReportType | null | undefined,
  timezone: string
) => {
  if (!reportConfig || !reportConfig.filterTypes || !customReportType) {
    const errorMessage = 'invalid report config';
    logger.logFatalMessage(errorMessage, reportConfig);
    throw new Error(errorMessage);
  }

  const reportCategory = getReportCategory(createSubscriptionPayload);
  const request = buildBaseCreateSubscriptionRequest(createSubscriptionPayload, reportConfig, timezone);
  const { currencyOfVisualization, marketplaceIds, timeUnit, mtaPickerChecked, amazonBusinessPickerChecked, mrcPickerChecked } =
    createSubscriptionPayload;
  const columns = getCustomReportColumns({
    reportCategory: createSubscriptionPayload.reportCategory as string,
    customReportType: customReportType,
    timeUnitId: timeUnit,
    cov: currencyOfVisualization,
    isMta: mtaPickerChecked
  });

  const columnTranslations = {};
  columns.forEach(col => ((columnTranslations as any)[col.id] = col.name));

  return {
    ...request,
    reportCategory,
    timeUnit: createSubscriptionPayload.timeUnit,
    currencyOfVisualization,
    columns: getColumnIds(columns),
    // Custom reports can include additional conditional filters
    filterTypes: customReportType.filterTypes,
    marketplaceIds: marketplaceIds,
    campaignSites: amazonBusinessPickerChecked ? [SITE_AMAZON_BUSINESS] : null,
    mrcAccreditationStatus: mrcPickerChecked ? MRC_ACCREDITATION_STATUS_FILTER_VALUE_SIVT : null,
    translationsMap: {
      ...columnTranslations,
      ...customReportType.translationsMap
    }
  };
};

export const getReportCategory = (createSubscriptionPayload: CreateSubscriptionState) => {
  let reportCategory = createSubscriptionPayload.reportCategory;
  // override SB with HSA
  if (reportCategory === REPORT_CATEGORY.SB) {
    reportCategory = REPORT_CATEGORY.HSA;
  }

  return reportCategory;
};

export const buildBaseCreateSubscriptionRequest = (
  createSubscriptionPayload: CreateSubscriptionState,
  reportConfig: ReportType | undefined,
  timezone: string
) => {
  if (
    !createSubscriptionPayload ||
    !createSubscriptionPayload.reportCategory ||
    !createSubscriptionPayload.reportType ||
    !createSubscriptionPayload.timeUnit ||
    !createSubscriptionPayload.reportName ||
    !createSubscriptionPayload.timeUnit ||
    !createSubscriptionPayload.subscriptionDelivery ||
    !createSubscriptionPayload.reportPeriod
  ) {
    const errorMessage = 'invalid create report data';
    logger.logFatalMessage(errorMessage, createSubscriptionPayload);
    throw new Error(errorMessage);
  }

  if (!reportConfig) {
    const errorMessage = 'invalid report config';
    logger.logFatalMessage(errorMessage, reportConfig);
    throw new Error(errorMessage);
  }

  const reportCategory = getReportCategory(createSubscriptionPayload);

  let reportPeriod = createSubscriptionPayload.reportPeriod?.period;

  let reportStartDate = null;
  let reportEndDate = null;
  if (createSubscriptionPayload.reportPeriod?.start && createSubscriptionPayload.reportPeriod?.end) {
    reportStartDate = dateTimeFns.getUnixTimeInMillis(new Date(createSubscriptionPayload.reportPeriod?.start));
    reportEndDate = dateTimeFns.getUnixTimeInMillis(new Date(createSubscriptionPayload.reportPeriod?.end));
  }

  // override TODAY with CUSTOM
  if (createSubscriptionPayload.reportPeriod?.period === PRESET_PERIOD.TODAY) {
    reportPeriod = PRESET_PERIOD.CUSTOM;
  }

  const reportMetadata = {
    reportCategoryId: reportCategory,
    reportTypeId: createSubscriptionPayload.reportType,
    templateId: reportConfig.templateId,
    templateGuidance: reportConfig.templateGuidance,
    timeUnitId: createSubscriptionPayload.timeUnit
  };

  return {
    reportName: createSubscriptionPayload.reportName,
    reportType: createSubscriptionPayload.reportType,
    reportPeriod,
    reportStartDate,
    reportEndDate,
    reportPeriodTimezone: timezone,
    subscriptionDelivery: buildUTCSubscriptionDelivery(createSubscriptionPayload.subscriptionDelivery, timezone),
    reportMetadata,
    filterTypes: reportConfig.filterTypes,
    recipients: createSubscriptionPayload.recipients
  };
};

const transformReportPeriod = (reportPeriod: string | undefined) => {
  if (reportPeriod && REPORT_PERIOD_ALIAS[reportPeriod.toUpperCase()]) {
    return REPORT_PERIOD_ALIAS[reportPeriod.toUpperCase()].toLowerCase();
  }
  return reportPeriod;
};

const transformSubscription = (subscription: any) => {
  const subscriptionDelivery = subscription.subscriptionDelivery;

  const transformedSubscriptionDelivery: SubscriptionDelivery = {
    frequency: subscriptionDelivery?.frequency,
    scheduledEndDate: subscriptionDelivery?.scheduledEndDate,
    deliveryDate: subscriptionDelivery?.deliveryDate,
    deliveryMonth: subscriptionDelivery?.deliveryMonth,
    deliveryYear: subscriptionDelivery?.deliveryYear,
    deliveryDays: subscriptionDelivery?.deliveryDays,
    hour: subscriptionDelivery?.hour,
    minute: subscriptionDelivery?.minute
  };

  let status = subscription.status;
  const reportStatus = subscription.latestProcessedReportSummary?.status;

  // subscription status should be same as report status if latest processed report is failed.
  if (reportStatus === SUBSCRIPTION_STATUS.FAILED) {
    status = reportStatus;
  }

  return {
    creationTimestamp: subscription.latestProcessedReportSummary?.completedAt,
    id: subscription.id,
    name: subscription.name,
    status,
    programType: subscription.programType,
    reportType: subscription.userReportType,
    accounts: subscription.accounts,
    nextScheduleTime: subscription.schedule?.nextScheduleTime,
    nextScheduleTimestamp: subscription.earliestUnprocessedReportSummary?.scheduledAt,
    recipients: subscription.publishers,
    reportPeriod: transformReportPeriod(subscription.config?.date_range),
    reportStartDate: subscription.config?.custom_start_date,
    reportEndDate: subscription.config?.custom_end_date,
    reportStatus,
    s3Url: subscription.latestProcessedReportSummary?.urlString,
    subscriptionDelivery: transformedSubscriptionDelivery,
    timeUnit: subscription.config?.default_interval,
    format: subscription.config?.format,
    currencyOfVisualization: subscription.config?.currencyOfVisualization,
    marketplaceIds: subscription.marketplaceIds,
    latestReportId: subscription.latestProcessedReportSummary?.id,
    hasMultiTouchAttribution: subscription.config?.hasMultiTouchAttribution,
    isMRCCheckboxSelected: isMRCCheckboxSelected(subscription.filters),
    isAmazonBusinessPickerChecked: isAmazonBusinessCheckboxSelected(subscription.filters)
  };
};

export const transformCreateSubscriptionResponse = (response: any): CreateSubscriptionResponse => ({
  subscriptionId: response
});

export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/reports/api'
  }),
  tagTypes: [Tag.SUBSCRIPTIONS],
  endpoints: builder => ({
    getSubscriptions: builder.query({
      query: (request: GetSubscriptionsRequest) => {
        return { url: `/subscriptions?entityId=${getEntityId()}`, method: HttpMethod.POST, data: request, headers: getHeaders() };
      },
      transformResponse: (response: any): GetSubscriptionsResponse => {
        return {
          subscriptions: response?.subscriptions.map(transformSubscription),
          totalRowCount: response.totalResults,
          nextToken: response.nextToken
        };
      },
      providesTags: [Tag.SUBSCRIPTIONS]
    }),
    // consider merging create and create custom?
    createSubscription: builder.mutation({
      query: (request: CreateSubscriptionRequest) => {
        return { url: `/subscriptions?entityId=${getEntityId()}`, method: HttpMethod.PUT, data: request, headers: getHeaders() };
      },
      transformResponse: transformCreateSubscriptionResponse
    }),
    createCustomSubscription: builder.mutation({
      query: (request: CreateCustomSubscriptionRequest) => {
        return { url: `/subscriptions/custom?entityId=${getEntityId()}`, method: HttpMethod.PUT, data: request, headers: getHeaders() };
      },
      transformResponse: transformCreateSubscriptionResponse
    }),
    deleteSubscription: builder.mutation({
      query: (subscriptionId: string) => {
        return {
          url: `/subscriptions?entityId=${getEntityId()}`,
          method: HttpMethod.DELETE,
          data: {
            id: subscriptionId
          },
          headers: getHeaders()
        };
      }
    }),
    updateSubscription: builder.mutation({
      query: (request: UpdateSubscriptionRequest) => {
        return { url: `/subscriptions?entityId=${getEntityId()}`, method: HttpMethod.PATCH, data: request, headers: getHeaders() };
      },
      async onQueryStarted(request: UpdateSubscriptionRequest, { dispatch, queryFulfilled }) {
        const getSubscriptionsRequest = buildGetSubscriptionByIdRequest(request.id);
        const subscriptionType = getSubscriptionTypeFromDelivery(request.subscriptionDelivery, getTimezone());
        // Override subscription status as expired for NOW to reflect new expected status in store.
        const status = subscriptionType === SUBSCRIPTION_TYPE.NOW ? SUBSCRIPTION_STATUS.EXPIRED : request.status;
        const patchResult = dispatch(
          subscriptionsApi.util.updateQueryData('getSubscriptions', getSubscriptionsRequest, draft => {
            draft.subscriptions[0] = {
              ...draft.subscriptions[0],
              name: request.reportName,
              status,
              subscriptionDelivery: request.subscriptionDelivery,
              recipients: request.recipients
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    updateSubscriptionStatus: builder.mutation({
      query: (request: UpdateSubscriptionStatusRequest) => {
        return { url: `/subscription-status?entityId=${getEntityId()}`, method: HttpMethod.PATCH, data: request, headers: getHeaders() };
      },
      async onQueryStarted(request: UpdateSubscriptionStatusRequest, { dispatch, queryFulfilled }) {
        const getSubscriptionsRequest = buildGetSubscriptionByIdRequest(request.id);
        const patchResult = dispatch(
          subscriptionsApi.util.updateQueryData('getSubscriptions', getSubscriptionsRequest, draft => {
            draft.subscriptions[0] = {
              ...draft.subscriptions[0],
              status: request.status
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    runSubscription: builder.mutation<RunSubscriptionResponse, string>({
      query: (subscriptionId: string) => {
        return {
          url: `/subscriptions/${subscriptionId}/report?entityId=${getEntityId()}`,
          method: HttpMethod.POST,
          headers: getHeaders()
        };
      }
    })
  })
});

export const selectGetSubscriptionsResult = (request: GetSubscriptionsRequest) =>
  subscriptionsApi.endpoints.getSubscriptions.select(request);

export const {
  useGetSubscriptionsQuery,
  useLazyGetSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useCreateCustomSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useUpdateSubscriptionStatusMutation,
  useRunSubscriptionMutation
} = subscriptionsApi;
